import { Button, Image, Link, Text, VStack } from '@chakra-ui/react';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import ErrorImage from 'assets/subscription/error.webp';
import serviceUnavailable from 'assets/common/serviceUnavailable.png';
import { ExternalRoutes } from 'types/Routes';
import { classifyError } from 'utils/classifyError';
import { captureClassifiedException } from 'utils/captureClassifiedException';
import { isSanctionedAccountError } from 'utils/getUserFacingError';

export const renderError = ({ error, resetErrorBoundary }: FallbackProps): ReactElement => (
  <Error error={error} resetErrorBoundary={resetErrorBoundary} />
);

const Error = ({ error, resetErrorBoundary }: FallbackProps): ReactElement => {
  const navigate = useNavigate();
  const [pathname, setPathname] = useState(window.location.pathname);
  const isSanctioned = isSanctionedAccountError(error);
  const classified = useMemo(() => classifyError(error, { layer: 'runtime' }), [error]);
  const { title, description } = classified.userMessage;

  const handleReset = (): void => {
    resetErrorBoundary();
  };

  const handleNavigate = (): void => {
    navigate(-1);
    resetErrorBoundary();
  };

  useEffect(() => {
    if (classified.shouldReport) {
      captureClassifiedException(error, { source: 'boundary', layer: 'runtime' });
    }
  }, [classified.shouldReport, error]);

  useEffect(() => {
    if (window.location.pathname !== pathname) {
      resetErrorBoundary();
    }
    setPathname(window.location.pathname);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetErrorBoundary, pathname, window.location.pathname]);

  return (
    <VStack gap="l" px="l" justifyContent="center" alignItems="center" width="100%">
      <Image src={isSanctioned ? serviceUnavailable : ErrorImage} />
      {isSanctioned ? (
        <>
          <Text textStyle="h1.medium" textAlign="center" color="text.primary">
            Anmeldung nicht möglich
          </Text>
          <Text textAlign="center" textStyle="h4.regular" color="text.secondary">
            Einige notwendige Angaben in deinem Profil fehlen. Deshalb kann die Registrierung in
            unserer App nicht abgeschlossen werden. Bitte kontaktiere unser Support-Team, um das
            Problem zu lösen.
          </Text>
          <Button type="button" variant="primary" w="full" maxWidth="350px">
            <Link href={ExternalRoutes.HELP} target="_blank" rel="noreferrer noopener">
              Support kontaktieren
            </Link>
          </Button>
        </>
      ) : (
        <>
          <Text textStyle="h1.medium" textAlign="center" color="text.primary">
            {title}
          </Text>
          <Text textAlign="center" textStyle="h4.regular" color="text.secondary">
            {description}
          </Text>
          <Button onClick={handleReset} type="button" variant="primary" w="full" maxWidth="350px">
            Versuchs noch Einmal
          </Button>
          <Button
            onClick={handleNavigate}
            type="button"
            variant="secondaryOutline"
            w="full"
            maxWidth="350px"
          >
            Zurück
          </Button>
          <Button type="button" variant="tertiary" w="full" maxWidth="350px">
            <Link href={ExternalRoutes.HELP} target="_blank" rel="noreferrer noopener">
              Support kontaktieren
            </Link>
          </Button>
        </>
      )}
    </VStack>
  );
};
